import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import DataTable from './DataTable';
import Loader from './Loader';

function RepairAnalytics() {
  const [assetCosts, setAssetCosts] = useState([]);
  const [monthlyCosts, setMonthlyCosts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchRepairAnalytics();
  }, []);

  const fetchRepairAnalytics = async () => {
    setLoading(true);
    try {
      const [assetRes, monthlyRes, summaryRes] = await Promise.all([
        axios.get('/api/repairanalytics/asset-repair-costs'),
        axios.get('/api/repairanalytics/monthly-repair-costs'),
        axios.get('/api/repairanalytics/repair-summary')
      ]);

      setAssetCosts(assetRes.data);
      setMonthlyCosts(monthlyRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching repair analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const assetColumns = [
    { field: 'assetName', label: 'Asset Name' },
    { field: 'assetType', label: 'Asset Type' },
    { 
      field: 'totalRepairCost', 
      label: 'Total Repair Cost',
      render: (value) => `₹${value.toLocaleString()}`
    },
    { field: 'repairCount', label: 'Repair Count' },
    { 
      field: 'avgRepairCost', 
      label: 'Avg Repair Cost',
      render: (value) => `₹${value.toLocaleString()}`
    },
    { 
      field: 'lastRepairDate', 
      label: 'Last Repair',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ];

  const COLORS = ['#667eea', '#764ba2', '#f093fb', '#f5576c', '#4facfe', '#00f2fe'];

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <Loader text="Loading repair analytics..." size="large" />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="page-title mb-0">
            <i className="fas fa-chart-line me-2"></i>Repair Cost Analytics
          </h1>
          <p className="text-muted">Comprehensive repair cost analysis and trends</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row mb-4">
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <i className="fas fa-rupee-sign fa-2x text-danger mb-2"></i>
                <h4 className="text-danger">₹{summary.totalRepairCost.toLocaleString()}</h4>
                <small className="text-muted">Total Repair Cost</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <i className="fas fa-tools fa-2x text-warning mb-2"></i>
                <h4 className="text-warning">{summary.totalRepairs}</h4>
                <small className="text-muted">Total Repairs</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <i className="fas fa-calculator fa-2x text-info mb-2"></i>
                <h4 className="text-info">₹{summary.avgRepairCost.toLocaleString()}</h4>
                <small className="text-muted">Avg Repair Cost</small>
              </div>
            </div>
          </div>
          <div className="col-md-3 col-sm-6 mb-3">
            <div className="card h-100 border-0 shadow-sm">
              <div className="card-body text-center">
                <i className="fas fa-calendar-month fa-2x text-success mb-2"></i>
                <h4 className="text-success">₹{summary.currentMonthCost.toLocaleString()}</h4>
                <small className="text-muted">This Month</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <i className="fas fa-chart-bar me-1"></i>Overview
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'assets' ? 'active' : ''}`}
            onClick={() => setActiveTab('assets')}
          >
            <i className="fas fa-laptop me-1"></i>Asset Costs
          </button>
        </li>
        <li className="nav-item">
          <button 
            className={`nav-link ${activeTab === 'monthly' ? 'active' : ''}`}
            onClick={() => setActiveTab('monthly')}
          >
            <i className="fas fa-calendar me-1"></i>Monthly Trends
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="row">
          <div className="col-md-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header">
                <h5><i className="fas fa-chart-line me-2"></i>Monthly Repair Costs Trend</h5>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyCosts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthName" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Cost']} />
                    <Line type="monotone" dataKey="totalCost" stroke="#667eea" strokeWidth={3} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header">
                <h5><i className="fas fa-chart-pie me-2"></i>Top 5 Expensive Assets</h5>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={assetCosts.slice(0, 5)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ assetName, totalRepairCost }) => `${assetName}: ₹${totalRepairCost.toLocaleString()}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="totalRepairCost"
                    >
                      {assetCosts.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Repair Cost']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="card border-0 shadow-sm">
          <div className="card-header">
            <h5><i className="fas fa-laptop me-2"></i>Asset Repair Costs</h5>
          </div>
          <div className="card-body">
            <DataTable
              data={assetCosts}
              columns={assetColumns}
              itemsPerPage={15}
            />
          </div>
        </div>
      )}

      {activeTab === 'monthly' && (
        <div className="row">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header">
                <h5><i className="fas fa-calendar me-2"></i>Monthly Repair Analysis</h5>
              </div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={monthlyCosts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="monthName" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'totalCost' ? `₹${value.toLocaleString()}` : value,
                        name === 'totalCost' ? 'Total Cost' : 'Repair Count'
                      ]} 
                    />
                    <Bar dataKey="totalCost" fill="#667eea" />
                    <Bar dataKey="repairCount" fill="#764ba2" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div className="col-12 mt-4">
            <div className="card border-0 shadow-sm">
              <div className="card-header">
                <h5><i className="fas fa-table me-2"></i>Monthly Data Table</h5>
              </div>
              <div className="card-body">
                <DataTable
                  data={monthlyCosts}
                  columns={[
                    { field: 'monthName', label: 'Month' },
                    { 
                      field: 'totalCost', 
                      label: 'Total Cost',
                      render: (value) => `₹${value.toLocaleString()}`
                    },
                    { field: 'repairCount', label: 'Repair Count' },
                    { 
                      field: 'avgCost', 
                      label: 'Average Cost',
                      render: (value) => `₹${value.toLocaleString()}`
                    }
                  ]}
                  itemsPerPage={12}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RepairAnalytics;